import React, { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../lib/supabase.js";
import { Modal, Loading, ErrorState } from "../ui/primitives.jsx";
import { fmtDate, todayStr } from "../lib/format.js";

const BAR_COLORS = ["#e8641b", "#2f6f9e", "#2e7d46", "#8e5aa8", "#b78300", "#b23a2b", "#3a7d74", "#5a6acf"];
const RENTAL_STATUS = { reserved: "Reserved", out: "On rent", returned: "Returned", cancelled: "Cancelled" };
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * Read-only port of v10's CalendarTab.
 *
 * One join replaces v10's three in-memory lookups: it needs equipment and
 * customer names per rental, and asking Postgres for them beats fetching three
 * whole tables to match ids in the browser.
 */
function useCalendarRentals() {
  return useQuery({
    queryKey: ["rentals", "calendar"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("rentals")
        .select("id,equipment_id,customer_id,start_date,end_date,status,paid,equipment(name),customers(name)")
        .neq("status", "cancelled")
        .order("start_date");
      if (error) throw new Error(error.message);
      return data;
    },
  });
}

function useEquipmentList() {
  return useQuery({
    queryKey: ["equipment", "names"],
    queryFn: async () => {
      const { data, error } = await supabase.from("equipment").select("id,name").order("name");
      if (error) throw new Error(error.message);
      return data;
    },
  });
}

export default function CalendarTab() {
  const [ym, setYm] = useState(todayStr().slice(0, 7));
  const [eqFilter, setEqFilter] = useState("all");
  const [dayDetail, setDayDetail] = useState(null);

  const rentals = useCalendarRentals();
  const equipment = useEquipmentList();

  const [y, m] = ym.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(y, m, 0).getDate();
  const t = todayStr();

  const eqIndex = (id) => (equipment.data ?? []).findIndex((e) => e.id === id);
  const eqColor = (id) => BAR_COLORS[Math.max(0, eqIndex(id)) % BAR_COLORS.length];
  const eqName = (r) => r.equipment?.name ?? "—";
  const custName = (r) => r.customers?.name ?? "—";

  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(`${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
  }

  // String comparison is safe here: ISO dates sort lexicographically.
  const barsFor = (dstr) =>
    (rentals.data ?? [])
      .filter((r) => r.start_date <= dstr && r.end_date >= dstr)
      .filter((r) => eqFilter === "all" || r.equipment_id === eqFilter);

  const shift = (n) => {
    const d = new Date(y, m - 1 + n, 1);
    setYm(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  if (rentals.isPending) return <div className="card"><div className="card-b"><Loading what="the schedule" /></div></div>;
  if (rentals.error) {
    return (
      <div className="card">
        <div className="card-b">
          <ErrorState error={rentals.error} onRetry={() => rentals.refetch()} />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="cal-head">
        <button className="btn ghost sm" onClick={() => shift(-1)} aria-label="Previous month">
          <ChevronLeft size={16} />
        </button>
        <h2>{first.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</h2>
        <button className="btn ghost sm" onClick={() => shift(1)} aria-label="Next month">
          <ChevronRight size={16} />
        </button>
        <button className="btn ghost sm" onClick={() => setYm(t.slice(0, 7))}>Today</button>
        <div className="spacer" style={{ flex: 1 }} />
        <select
          value={eqFilter}
          onChange={(e) => setEqFilter(e.target.value)}
          style={{ border: "1px solid var(--line)", borderRadius: 6, padding: "7px 10px" }}
        >
          <option value="all">All equipment</option>
          {(equipment.data ?? []).map((e) => (
            <option key={e.id} value={e.id}>{e.name}</option>
          ))}
        </select>
      </div>

      <div className="cal-grid">
        {DOW.map((d) => <div key={d} className="cal-dow">{d}</div>)}
        {cells.map((dstr, i) => {
          if (!dstr) return <div key={"pad" + i} className="cal-cell dim" />;
          const bars = barsFor(dstr);
          return (
            <div key={dstr} className={"cal-cell" + (dstr === t ? " today" : "")}>
              <span className="dnum">{Number(dstr.slice(8))}</span>
              {bars.slice(0, 3).map((r) => (
                <div
                  key={r.id}
                  className="cal-bar"
                  style={{ background: eqColor(r.equipment_id), opacity: r.status === "reserved" ? 0.65 : 1 }}
                  title={`${eqName(r)} — ${custName(r)}`}
                  onClick={() => setDayDetail(dstr)}
                >
                  {eqName(r).split(" ")[0]} · {custName(r).split(" ")[0]}
                </div>
              ))}
              {bars.length > 3 && (
                <div className="cal-more" onClick={() => setDayDetail(dstr)}>
                  +{bars.length - 3} more
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="legend">
        {(equipment.data ?? []).map((e, i) => (
          <span key={e.id}>
            <i style={{ background: BAR_COLORS[i % BAR_COLORS.length] }} />
            {e.name}
          </span>
        ))}
        <span>
          <i style={{ background: "#999", opacity: 0.6 }} />
          Lighter bar = reserved, solid = on rent
        </span>
      </div>

      <div className="notice" style={{ marginTop: 16 }}>
        <b>Website sync (Phase 3):</b> this calendar is the single source of truth.
        Once the customer site's booking form is connected, requests will land here as
        "Reserved," and booked dates here will show as unavailable on
        halcoequipmentrentals.com.
      </div>

      {dayDetail && (
        <Modal title={fmtDate(dayDetail)} onClose={() => setDayDetail(null)}>
          {barsFor(dayDetail).map((r) => (
            <div key={r.id} className="attrow">
              <i style={{ width: 11, height: 11, borderRadius: 3, background: eqColor(r.equipment_id), flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <b>{eqName(r)}</b> — {custName(r)}
                <div className="sub-cell">
                  {fmtDate(r.start_date)} → {fmtDate(r.end_date)} · {RENTAL_STATUS[r.status]} ·{" "}
                  {r.paid ? "Paid" : "Unpaid"}
                </div>
              </div>
            </div>
          ))}
          {barsFor(dayDetail).length === 0 && <div className="empty">Nothing booked this day.</div>}
        </Modal>
      )}
    </>
  );
}
